/**
 * Record's teleprompter band (Brian's record.html v3.1, Oct 22 2026): the script
 * moves up past a reading line 30% down the band at the creator's speed while a
 * take records. Tap pauses (tap again to go on), hold pauses while held, drag
 * scrubs. On resume the text rolls back half a line and the paragraph lights.
 * The Slow and Fast pads multiply the speed and chase a target (2x on Fast, a
 * quarter on Slow), quicker pushed than released, so a release coasts back.
 */

export const READ = 0.3
const PMAX = 2, PMIN = 0.25, PUSH = 0.45, COAST = 0.7

export interface PrompterState {
  paused: boolean
  holdPause: boolean
  clipPause: boolean
  /** The live speed multiplier from the pads. */
  m: number
}

export class Prompter {
  band: HTMLElement | null = null
  txt: HTMLElement | null = null
  readline: HTMLElement | null = null
  readdim: HTMLElement | null = null
  offset = 0
  paused = true
  holdPause = false
  clipPause = false
  m = 1
  target = 1
  words = 0
  wpm = 130
  private lastT = 0
  onChange?: (s: PrompterState) => void
  private sig = ''

  attach(band: HTMLElement | null, txt: HTMLElement | null, readline: HTMLElement | null, readdim: HTMLElement | null) {
    this.band = band; this.txt = txt; this.readline = readline; this.readdim = readdim
    this.setOffset(this.offset)
  }

  private bandH() { return this.band?.clientHeight ?? 0 }
  private contentH() { return this.txt?.scrollHeight ?? 0 }

  setOffset(o: number) {
    this.offset = Math.max(0, Math.min(o, this.contentH() + 10))
    if (!this.txt) return
    this.txt.style.transform = `translateY(${this.bandH() * READ - this.offset}px)`
    const lh = parseFloat(getComputedStyle(this.txt).lineHeight) || 35
    const y = Math.round(this.bandH() * READ + lh / 2)
    if (this.readline) this.readline.style.top = `${y}px`
    if (this.readdim) this.readdim.style.height = `${Math.max(0, y - 15)}px`
  }

  private pxPerSec() { return this.contentH() / Math.max(1, (this.words / Math.max(1, this.wpm)) * 60) }

  /** Called every frame. `running` is true while a take records. */
  tick(now: number, running: boolean) {
    const dt = Math.min(0.1, (now - (this.lastT || now)) / 1000)
    this.lastT = now
    if (!running) this.target = 1
    this.m += (this.target - this.m) * (1 - Math.exp(-dt / (this.target === 1 ? COAST : PUSH)))
    if (this.target === 1 && Math.abs(this.m - 1) < 0.005) this.m = 1
    if (running && !this.paused && !this.holdPause && !this.clipPause) this.setOffset(this.offset + dt * this.pxPerSec() * this.m)
    this.emit()
  }

  private emit() {
    const sig = `${this.paused}|${this.holdPause}|${this.clipPause}|${Math.round(this.m * 40)}`
    if (sig === this.sig) return
    this.sig = sig
    this.onChange?.({ paused: this.paused, holdPause: this.holdPause, clipPause: this.clipPause, m: this.m })
  }

  /** The paragraph at the reading line lights amber for a moment. */
  highlightHere() {
    if (!this.txt) return
    const mid = this.offset - this.bandH() * 0.05
    let best: HTMLElement | null = null
    for (const p of Array.from(this.txt.children) as HTMLElement[]) if (p.offsetTop <= mid + 4) best = p
    this.txt.querySelectorAll('.here').forEach((p) => p.classList.remove('here'))
    if (best) { const b = best; b.classList.add('here'); setTimeout(() => b.classList.remove('here'), 1800) }
  }

  resume() { this.setOffset(this.offset - 15); this.highlightHere(); this.paused = false; this.clipPause = false; this.emit() }
  pause() { this.paused = true; this.emit() }

  /** Hold a pad: Fast (1) or Slow (-1); 0 lets go. */
  pad(dir: -1 | 0 | 1) {
    this.target = dir === 1 ? PMAX : dir === -1 ? PMIN : 1
    if (dir && this.paused && !this.clipPause) this.resume()
  }

  reset() { this.paused = true; this.holdPause = false; this.clipPause = false; this.m = 1; this.target = 1; this.setOffset(0); this.emit() }

  /** The paragraph index at the reading line (the editor puts its cursor there). */
  paragraphAt(): number {
    if (!this.txt) return 0
    const mid = this.offset + 4
    let i = 0
    Array.from(this.txt.children).forEach((p, k) => { if ((p as HTMLElement).offsetTop <= mid) i = k })
    return i
  }
}

export const splitParas = (s: string) => s.split(/\n\s*\n/).map((p) => p.trim()).filter(Boolean)
export const countWords = (s: string) => s.split(/\s+/).filter(Boolean).length
export const mmss = (s: number) => `${Math.floor(s / 60)}:${String(Math.floor(s % 60)).padStart(2, '0')}`
/** One time, in plain words, on the script (v3, Linda). */
export const readTime = (words: number, wpm: number) => {
  const sec = (words / wpm) * 60
  return sec < 55 ? `about ${Math.max(10, Math.round(sec / 5) * 5)} sec` : `about ${Math.round(sec / 60)} min`
}
